import {
    bindQuerySelector,
    type ElementProps,
    jsx,
    type JsxChild,
    onConnect,
    setClassToken,
} from '@amazon/vinyl-tsx'
import { Icon } from '@/components/Icon'
import { type Maybe, roundToNearest } from '@amazon/vinyl-util'
import type { MaybeObservableValue } from '@amazon/vinyl-observable'
import { omit } from '@/util/objectUtil'
import { onResize } from '@/util/domUtil'
import { getString } from '@/browse/i18n/bundle.generated'

export type ShovelerProps = ElementProps<'div'> & {
    readonly header?: Maybe<Node>
    /**
     * Parameterizes the previous/next chevron accessibility labels —
     * "View previous in {text}" / "View next in {text}". Named
     * `headerTitle` to avoid clashing with the native HTML `title`
     * attribute that `ElementProps<'div'>` already defines as `string`.
     */
    readonly headerTitle?: MaybeObservableValue<string>
}

export function Shoveler(props: ShovelerProps, children: readonly JsxChild[]) {
    // These props will be calculated from the css:
    let cols = 1
    let columnAndGapWidth = 0
    let maxScrollX = 0

    const titleParam = props.headerTitle ?? ''
    const prevLabel = getString('dmusic_visual_shoveler_previous_button_accessibility_label', {
        text: titleParam,
    })
    const nextLabel = getString('dmusic_visual_shoveler_next_button_accessibility_label', {
        text: titleParam,
    })

    const container = <div className="itemsContainer">{...children}</div>
    const view = (
        <div {...omit(props, ['header', 'headerTitle'])} className={`shoveler ${props.className}`}>
            <div className={'header'}>
                {props.header}
                <div className={'controls'}>
                    <button
                        className="button previousBtn"
                        onclick={scrollLeft}
                        aria-label={prevLabel}
                    >
                        <Icon icon="caretLeft" />
                    </button>
                    <button className="button nextBtn" onclick={scrollRight} aria-label={nextLabel}>
                        <Icon icon="caretRight" />
                    </button>
                </div>
            </div>
            {container}
        </div>
    )

    const viewEl = bindQuerySelector(view)
    const previousBtn = viewEl('.previousBtn')
    const nextBtn = viewEl('.nextBtn')
    const controls = viewEl('.controls')

    let targetX = 0
    let usingButtons = 0
    let viewportCols = 1

    /**
     * Measures the column sizes and sets columnWidth and maxScrollX
     */
    function measure() {
        const computedStyle = window.getComputedStyle(container)
        const rows = computedStyle.getPropertyValue('grid-template-rows').split(' ')

        cols = Math.ceil(children.length / rows.length)

        const columns = computedStyle.getPropertyValue('grid-template-columns').split(' ')

        const firstColumnWidth = parseFloat(columns[0])
        if (!firstColumnWidth) return

        const columnGap = parseFloat(computedStyle.getPropertyValue('column-gap'))

        columnAndGapWidth = firstColumnWidth + columnGap

        viewportCols = Math.max(1, Math.floor(container.clientWidth / columnAndGapWidth))
        const minColumnsToShow = Math.max(viewportCols - 1, 1)
        maxScrollX = (cols - minColumnsToShow) * columnAndGapWidth
    }

    function scrollLeft() {
        scrollTo(Math.max(0, targetX - columnAndGapWidth * viewportCols))
    }

    function scrollRight() {
        scrollTo(Math.min(maxScrollX, targetX + columnAndGapWidth * viewportCols))
    }

    function scrollTo(newX: number) {
        targetX = roundToNearest(newX + columnAndGapWidth * 0.2, columnAndGapWidth)
        container.scrollTo({ left: targetX, behavior: 'smooth' })
        refreshScrollButtons()
        usingButtons++
        setTimeout(() => {
            usingButtons--
        }, 1000)
    }

    const onScroll = () => {
        if (!usingButtons) {
            // Set targetX so the buttons pick up from current column.
            targetX = container.scrollLeft
            refreshScrollButtons()
        }
    }
    container.onscroll = onScroll

    let prevDisabled = false
    let nextDisabled = false
    let overflows = false
    function refreshScrollButtons() {
        const newPrevDisabled = targetX <= 20
        if (prevDisabled !== newPrevDisabled) {
            prevDisabled = newPrevDisabled
            setClassToken(previousBtn, 'disabled', newPrevDisabled)
            previousBtn.tabIndex = newPrevDisabled ? -1 : 0
        }
        const newNextDisabled = targetX >= maxScrollX - 20
        if (nextDisabled !== newNextDisabled) {
            nextDisabled = newNextDisabled
            setClassToken(nextBtn, 'disabled', newNextDisabled)
            nextBtn.tabIndex = newNextDisabled ? -1 : 0
        }
        // If the row fits in the viewport there's nothing to scroll —
        // hide the chevrons entirely so the header doesn't carry inert
        // controls. Mirrors hornet's `shouldShowArrows` gate.
        const newOverflows = maxScrollX > 20
        if (overflows !== newOverflows) {
            overflows = newOverflows
            setClassToken(controls, 'hidden', !newOverflows)
        }
    }
    refreshScrollButtons()

    onConnect(container, () => {
        const computedStyle = window.getComputedStyle(container)
        const rows = computedStyle.getPropertyValue('grid-template-rows').split(' ')
        if (children.length < rows.length) {
            container.style.gridTemplateRows = rows.slice(0, children.length).join(' ')
        }

        // Restore horizontal scroll position on navigation:
        container.scrollLeft = targetX

        return onResize(container, () => {
            measure()
            onScroll()
        })
    })

    return view
}
